import { noop } from 'es-toolkit';
import { Queue } from '../../../utils/queue';
import { isServer } from '@skin-seoul/env-utils';

let instance: CartTokenQueue | null = null;

class CartTokenQueue extends Queue<string> {
  private readonly MAX_TOKENS = 10;

  /**
   * Adds a new token
   * @param token Token to add
   */
  override enqueue(token: string): void {
    if (!token) {
      return;
    }

    const latestToken = this.peekLatest();
    if (latestToken === token) {
      return;
    }

    super.enqueue(token);

    while (this.size() > this.MAX_TOKENS) {
      super.dequeue();
    }
  }

  /**
   * Returns the most recently added token
   * Custom implementation because Queue's peek() returns the oldest element
   */
  peekLatest(): string | undefined {
    if (this.isEmpty()) {
      return undefined;
    }

    const itemsString = this.toString();
    if (!itemsString) {
      return undefined;
    }

    const items = itemsString.split(',');
    return items[items.length - 1];
  }

  /**
   * Returns the most recent token (overriding peek method for API compatibility)
   */
  override peek(): string | undefined {
    const latestToken = this.peekLatest();

    return latestToken;
  }
}

/**
 * Function that returns a singleton instance
 * Returns a dummy implementation on the server and a real implementation on the client
 */
function getCartTokenQueue(): CartTokenQueue {
  if (isServer()) {
    return {
      enqueue: noop,
      peek: () => undefined,
      peekLatest: () => undefined,
      dequeue: () => undefined,
      clear: noop,
      isEmpty: () => true,
      size: () => 0,
      toString: () => '',
    } as unknown as CartTokenQueue;
  }

  if (!instance) {
    instance = new CartTokenQueue();
  }

  return instance;
}

const cartTokenQueue = getCartTokenQueue();

export { cartTokenQueue };
